package editor

import common.dto.*
import editor.keyboard.KeyboardManager
import editor.structure.StructureElement
import ext.*
import three.js.Euler
import three.js.Quaternion
import three.js.Vector3
import util.DebugManager
import util.RotationHelper
import kotlin.math.PI
import kotlin.math.abs

/**
 * Helper for calculating the position and rotation (quaternion) of a new element
 * based on the target element, to-be-created element type and intersection point
 * of the ray through the mouse pointer onto the target element.
 */
object PlaceManager {
    /**
     * By default, the elements should snap to a grid with a step size of one brixi length. When
     * the Shift key is pressed, we quarter the step size to 8.
     */
    private val stepSize get() = if (KeyboardManager.shiftPressed) 4.0 else 16.0

    /**
     * Calculate the direction for the [toBePlaced] element based on e.g. if the Control key is pressed.
     *
     * A brick gets rotated when the Control key is pressed, so that the long side is orthogonal to the surface
     * of the target element.
     */
    private fun getDirectionForPlaced(toBePlaced: ElementType): Direction = when (toBePlaced) {
        ElementType.Brixi, ElementType.Twister -> Direction.FORWARD
        ElementType.Wheels -> if (KeyboardManager.ctrlPressed) {
            if (KeyboardManager.altPressed) Direction.RIGHT else Direction.LEFT
        } else Direction.FORWARD
        else -> if (KeyboardManager.ctrlPressed) Direction.UP else Direction.FORWARD // bricks
    }

    /**
     * Calculate the position of the new element:
     *
     * 1. Get the direction in which the new element should be placed (relative to the un-rotated new element, i.e. its model).
     *    This can be either toward positive z or positive y (relative to the un-rotated model), depending
     *    on whether the Ctrl key is pressed (for rotating the bricks).
     * 2. Calculate the distance from the center of the target element to the center of the new element.
     *    This is half of the height of the target element plus half of the height of the new element.
     *    For these heights, the diameter of the element in the direction of the placement is used.
     * 3. Add the offset to the position. For this, the 2d offset is first potentially modified by the [ElementType],
     *    and then rounded to the nearest multiple of 4 or 8 (depending on the Shift key).
     *
     * We calculate the resulting distance to the target element position in the local space of the *new un-rotated
     * element* (this means we simulate placing the new element on the negative z-side of the target element),
     * and then, at the end, map it to the local space of the *target element* by applying the quaternion
     * of the local direction relative to the target element.
     */
    // todo make it ... actually readable
    private fun calculatePosition(
        target: StructureElement,
        toBePlacedType: ElementType,
        localDirection: Direction,
        offset: Vec2d,
        currentScrollRotation: Int,
    ): Pair<Vec3d, Quat> {
        val directionForPlaced = getDirectionForPlaced(toBePlacedType)
        val distance = (target.type.heightFor(localDirection) / 2) + (toBePlacedType.heightFor(directionForPlaced) / 2)
        val localPosition = Vec3d(z = 1.0) * distance

        val modifiedOffset = target.type.modifyOffset(toBePlacedType, offset)
        val steppedOffset = modifiedOffset.roundStepped(stepSize)
        val offset3d = Vector3(steppedOffset.x, steppedOffset.y, 0.0)

        // rotate pos according to currentScrollRotation
        val rotAround = target.type.getRotationAround(toBePlacedType, currentScrollRotation, directionForPlaced, offset3d.asVec3d())

        // it's because of the offset! the offset depends on whether the brick is z-rotated or not
        val customOffset = if (abs(offset.x) > abs(offset.y)) {
            offset3d.applyQuaternion(Quaternion().setFromEuler(Euler(0.0, 0.0, -PI / 2)).normalize())
        } else offset3d
        val (rotatedPosDelta2, rot) = RotationHelper.applyRotationAround(customOffset.asVec3d(), rotAround)
        val rotatedPosDelta = if (abs(offset.x) > abs(offset.y)) {
            rotatedPosDelta2.asVector3().applyQuaternion(Quaternion().setFromEuler(Euler(0.0, 0.0, PI / 2)).normalize()).asVec3d()
        } else rotatedPosDelta2

        DebugManager.debug("localPosition: $localPosition")
        DebugManager.debug("axis: ${rotAround.line.origin} -> ${rotAround.line.direction}, angle: ${rotAround.angle}")
        DebugManager.debug("rotatedPos: $rotatedPosDelta, rot: $rot")

        val mappedPos = (rotatedPosDelta + localPosition).asVector3().applyQuaternion(localDirection.quaternion.asQuaternion())
        return target.object3D.localToWorld(mappedPos).asVec3d() to rot
    }

    /**
     * Calculate the 2d offset of the intersection point to the center of the intersected plane on the
     * target element, in the local space of the *plane*. This means, that the offset behaves the same,
     * no matter how the target is rotated and what side of the target we are looking at.
     *
     * For this, we first calculate the local position of the intersection point relative to the target element.
     * Then, we apply the inverse quaternion of the local direction (from which direction are we looking at the target)
     * to the local position. This way, we get the offset in the local space of the plane. The z-component of the
     * resulting vector is always the distance from the plane to the target center position. Extracting the x and y
     * components gives us the 2d offset on the plane.
     *
     * In the PLACING state, we extend the hypothetical/invisible plane to allow any desired offset.
     */
    private fun calculateOffset(
        target: StructureElement,
        localDirection: Direction,
        worldIntersectionPoint: Vector3,
    ): Vec2d {
        // we need to use worldToLocal instead of [minus target position] to also consider the rotation of the target element
        val localPoint = target.object3D.worldToLocal(worldIntersectionPoint)
        val mappedPoint = localPoint.applyQuaternion(localDirection.quaternion.asQuaternion().invert())

        return Vec2d(mappedPoint.x.toDouble(), mappedPoint.y.toDouble())
    }

    /**
     * Calculate the quaternion rotation of the new element:
     *
     * **target quaternion * local direction quaternion * [maybe 90° turn] * [additional to-be-placed rotation]**
     *
     * [Maybe 90° turn]: based on the offset of the intersection point on the target element, we rotate the new element
     * by 90 degrees around the z-axis if the offset in x is larger than the offset in y. This works because by default,
     * how the offset is calculated and the models of the bricks are oriented, the offset y-axis
     * corresponds to the long side of a brick. Thus, if abs(x) > abs(y), we want the brick to have the long side
     * oriented along the x-axis.
     *
     * [Additional to-be-placed rotation]: E.g. when the control key is pressed,
     * the new element should be rotated so that the long side is orthogonal to the surface of the target element.     *
     *
     * @param target the target element on which the new element should be placed
     * @param toBePlacedType the type of the new element that should be placed. We use this to determine the
     *                       *additional* rotation that should be applied to the new element
     *                       (e.g. when the control key is pressed)
     * @param localDirection the direction of the normal vector on the intersected plane of the target element
     *                       in the local space of the target element
     * @param currentScrollRotation the current rotation of the scroll wheel, which is used to e.g. rotate the brixi
     *                              in the placing state when placing upon a twister
     */
    private fun calculateQuaternion(
        target: StructureElement,
        toBePlacedType: ElementType,
        localDirection: Direction,
        offset: Vec2d,
        currentScrollRotation: Int,
        rotAroundQuat: Quat,
    ) = Quaternion().apply {
        multiply(target.object3D.quaternion).normalize()
        multiply(localDirection.quaternion.asQuaternion()).normalize()
        if (abs(offset.x) > abs(offset.y)) {
            multiply(Quaternion().setFromEuler(Euler(0.0, 0.0, PI / 2)).normalize())
        }
        // todo actually rotate the brixi around that line
        //      -> we also need to update the position!
//        val rotAround = toBePlacedType.getRotationAround(toBePlacedType, currentScrollRotation)
//        val (deltaPos, rot) = RotationHelper.applyRotationAround(, rotAround)
        multiply(rotAroundQuat.asQuaternion()).normalize()

        multiply(getDirectionForPlaced(toBePlacedType).quaternion.asQuaternion()).normalize()
    }.asQuat()

    /**
     * Returns a new element that would be placed on the target element with the given local direction.
     *
     * @param localDirection direction of the normal vector on the face of the target element
     * (in the local space of the target element).
     *
     * @param intersectionPoint when in the PLACING state, we construct an additional plane and intersect it
     *                          with the cursor ray to get the intersection point (and allow for a larger offset).
     *                          When in HOVERING, this is simply the intersection point for the intersection with
     *                          the target element. In world coordinates.
     */
    fun placeElement(
        target: StructureElement,
        localDirection: Direction,
        toBePlacedType: ElementType,
        intersectionPoint: Vector3,
        currentScrollRotation: Int,
    ): StructureElement? {
        DebugManager.debug("intersectionPoint: ${intersectionPoint.asVec3d()}")

        if (!target.type.allowsPlacement(localDirection, toBePlacedType)) return null

        val offset = calculateOffset(target, localDirection, intersectionPoint)
        DebugManager.debug("Offset: $offset")
        val (worldPosition, rotAroundQuat) = calculatePosition(target, toBePlacedType, localDirection, offset, currentScrollRotation)
        val worldQuaternion = calculateQuaternion(target, toBePlacedType, localDirection, offset, currentScrollRotation, rotAroundQuat)

        return StructureElement(toBePlacedType, worldPosition, worldQuaternion)
    }
}